import { MEMBER_ROLES, memberRoleLabel } from '@kanvas/board-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  lmsApi,
  type MemberKind,
  type PersonBoard,
  type RosterMember,
  type StudentTask,
} from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDateShort, toDateInput } from './dates';

/**
 * A 403 is the server saying "not yours to see" — a curator opening a student
 * they do not teach — and reads as an empty list, not as a failed load.
 */
const isRefusal = (e: unknown) => e instanceof ApiError && e.status === 403;

export interface PersonProfileModalProps {
  orgId: string;
  person: RosterMember;
  /** The whole roster, to offer teachers for a student and students for a teacher. */
  roster: RosterMember[];
  /** Only the organisation's owner may change roles, links and payments. */
  canManage: boolean;
  onSetKind: (userId: string, kind: MemberKind) => Promise<void>;
  onSetTeachers: (studentId: string, teacherIds: string[]) => Promise<void>;
  onSetStudents: (teacherId: string, studentIds: string[]) => Promise<void>;
  onSetPayment: (userId: string, patch: PaymentPatch) => Promise<void>;
  /** Opens the task editor with this student preselected. */
  onGiveTask: (studentId: string) => void;
  onOpenTask: (taskId: string) => void;
  onOpenBoard: (boardId: string) => void;
  onClose: () => void;
}

export interface PaymentPatch {
  paid?: boolean;
  lessonsCount?: number | null;
  paymentAmount?: number | null;
  paidAt?: number | null;
}

/**
 * A number field that does not round-trip on every keystroke.
 *
 * Saving on `onChange` disabled the input (and stole focus) after one digit,
 * which is how "1500" became four separate edits. The parent still owns the
 * committed value; this only holds the draft while the field is focused.
 */
function NumericDraftInput({
  value,
  placeholder,
  integer,
  onCommit,
}: {
  value: number | null | undefined;
  placeholder?: string;
  integer?: boolean;
  onCommit: (next: number | null) => void;
}) {
  const [text, setText] = useState(() => (value == null ? '' : String(value)));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setText(value == null ? '' : String(value));
  }, [value]);

  const commit = () => {
    const trimmed = text.trim().replace(',', '.');
    if (trimmed === '') {
      if (value != null) onCommit(null);
      setText('');
      return;
    }
    const next = Number(trimmed);
    if (!Number.isFinite(next) || next < 0) {
      setText(value == null ? '' : String(value));
      return;
    }
    const committed = integer ? Math.round(next) : next;
    setText(String(committed));
    if (committed !== value) onCommit(committed);
  };

  return (
    <input
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      placeholder={placeholder}
      value={text}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

/**
 * One person's card: who they are, who they are attached to, what they owe
 * and what they have been set.
 *
 * The list behind it stays deliberately plain — a name and a link — because
 * the detail belongs to one person at a time, and putting payment fields into
 * every table row turns a roster into a spreadsheet nobody can scan.
 */
export function PersonProfileModal({
  orgId,
  person,
  roster,
  canManage,
  onSetKind,
  onSetTeachers,
  onSetStudents,
  onSetPayment,
  onGiveTask,
  onOpenTask,
  onOpenBoard,
  onClose,
}: PersonProfileModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [tasks, setTasks] = useState<StudentTask[] | null>(null);
  const [boards, setBoards] = useState<PersonBoard[] | null>(null);
  /** Which of the two lists failed to load — an empty list is not the same thing. */
  const [tasksFailed, setTasksFailed] = useState(false);
  const [boardsFailed, setBoardsFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const teachers = useMemo(() => roster.filter((m) => m.kind === 'teacher'), [roster]);
  const students = useMemo(() => roster.filter((m) => m.kind === 'student'), [roster]);
  const isStudent = person.kind === 'student';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A teacher's own homework list is visible to them; the owner sees all of
  // it. Either way a failure here must not blank the card.
  useEffect(() => {
    if (!isStudent) {
      setTasks(null);
      return;
    }
    let cancelled = false;
    setTasksFailed(false);
    void lmsApi
      .studentTasks(orgId, person.userId)
      .then((list) => {
        if (!cancelled) setTasks(list);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTasks([]);
        setTasksFailed(!isRefusal(e));
      });
    return () => {
      cancelled = true;
    };
  }, [isStudent, orgId, person.userId]);

  useEffect(() => {
    let cancelled = false;
    setBoards(null);
    setBoardsFailed(false);
    void lmsApi
      .userBoards(orgId, person.userId)
      .then((list) => {
        if (!cancelled) setBoards(list);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setBoards([]);
        setBoardsFailed(!isRefusal(e));
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, person.userId]);

  /** Keeps the card responsive while a patch is in flight. */
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  const toggleLink = (id: string, current: string[], apply: (next: string[]) => Promise<void>) => {
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    void run(() => apply(next));
  };

  const pending = tasks?.filter((t) => !t.done).length ?? 0;

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (!dialogRef.current?.contains(e.target as Node)) onClose();
      }}
    >
      <div
        className="profile-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={person.name}
      >
        <header className="profile-head">
          <span className="profile-avatar" style={{ background: person.color }}>
            {person.name.slice(0, 1).toUpperCase()}
          </span>
          <div className="profile-ident">
            <h2>{person.name}</h2>
            <p>{person.email}</p>
            <div className="profile-tags">
              <span className={`kind-badge is-${person.kind ?? 'none'}`}>
                {memberRoleLabel(person.kind) ?? 'Без роли'}
              </span>
              {person.role === 'admin' && <span className="kind-badge is-owner">Владелец</span>}
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="profile-body">
          {canManage && (
            <section className="profile-section">
              <h3>Роль</h3>
              {/* Driven by the shared role directory, so a role added there
                  is assignable here without touching this screen. */}
              <div className="kind-switch">
                {[...MEMBER_ROLES.map((role) => role.slug), null].map((kind) => (
                  <button
                    key={kind ?? 'none'}
                    type="button"
                    className={person.kind === kind ? 'is-active' : ''}
                    disabled={busy}
                    onClick={() => void run(() => onSetKind(person.userId, kind))}
                  >
                    {memberRoleLabel(kind) ?? 'Без роли'}
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="profile-section">
            <h3>
              <Icon name="boards" size={14} />
              Доски
              {boards && boards.length > 0 && (
                <em className="field-hint"> {boards.length}</em>
              )}
            </h3>
            {boards === null ? (
              <p className="org-members-hint">Загрузка…</p>
            ) : boardsFailed ? (
              <p className="form-error" role="alert">
                Не удалось загрузить доски
              </p>
            ) : boards.length === 0 ? (
              <p className="org-members-hint">Досок, которые вам доступны, пока нет.</p>
            ) : (
              <ul className="profile-tasks">
                {boards.map((board) => (
                  <li key={board.id}>
                    <button type="button" onClick={() => onOpenBoard(board.id)}>
                      <span className="profile-task-title">{board.name}</span>
                      <em>{board.relation === 'owner' ? 'создатель' : 'участник'}</em>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* A teacher has no roster of their own, so the link editors are the
              owner's alone — for anyone else they would render as empty lists. */}
          {isStudent && canManage && (
            <section className="profile-section">
              <h3>
                <Icon name="graduation" size={14} />
                Закреплён за
              </h3>
              {teachers.length === 0 ? (
                <p className="org-members-hint">В организации пока нет учителей.</p>
              ) : (
                <ul className="link-picker">
                  {teachers.map((teacher) => (
                    <li key={teacher.userId}>
                      <label>
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={person.teacherIds.includes(teacher.userId)}
                          onChange={() =>
                            toggleLink(teacher.userId, person.teacherIds, (next) =>
                              onSetTeachers(person.userId, next),
                            )
                          }
                        />
                        <span className="org-member-avatar" style={{ background: teacher.color }}>
                          {teacher.name.slice(0, 1).toUpperCase()}
                        </span>
                        {teacher.name}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {person.kind === 'teacher' && canManage && (
            <section className="profile-section">
              <h3>
                <Icon name="users" size={14} />
                Ученики
              </h3>
              {students.length === 0 ? (
                <p className="org-members-hint">В организации пока нет учеников.</p>
              ) : (
                <ul className="link-picker">
                  {students.map((student) => (
                    <li key={student.userId}>
                      <label>
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={person.studentIds.includes(student.userId)}
                          onChange={() =>
                            toggleLink(student.userId, person.studentIds, (next) =>
                              onSetStudents(person.userId, next),
                            )
                          }
                        />
                        <span className="org-member-avatar" style={{ background: student.color }}>
                          {student.name.slice(0, 1).toUpperCase()}
                        </span>
                        {student.name}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {isStudent && canManage && (
            <section className="profile-section">
              <h3>
                <Icon name="wallet" size={14} />
                Оплата
              </h3>
              <label className="profile-paid">
                <input
                  type="checkbox"
                  checked={person.paid}
                  disabled={busy}
                  onChange={(e) =>
                    void run(() => onSetPayment(person.userId, { paid: e.target.checked }))
                  }
                />
                <span>{person.paid ? 'Оплачено' : 'Не оплачено'}</span>
                {person.paid && person.paidAt && (
                  <em>от {formatDateShort(new Date(person.paidAt).toISOString())}</em>
                )}
              </label>

              <div className="profile-grid">
                <label className="field">
                  <span>Дата оплаты</span>
                  <input
                    type="date"
                    value={person.paidAt ? toDateInput(new Date(person.paidAt).toISOString()) : ''}
                    disabled={busy}
                    onChange={(e) =>
                      void run(() =>
                        onSetPayment(person.userId, {
                          paidAt: e.target.value ? new Date(e.target.value).getTime() : null,
                        }),
                      )
                    }
                  />
                </label>
                <label className="field">
                  <span>Занятий</span>
                  <NumericDraftInput
                    integer
                    placeholder="—"
                    value={person.lessonsCount}
                    onCommit={(lessonsCount) => void onSetPayment(person.userId, { lessonsCount })}
                  />
                </label>
                <label className="field">
                  <span>Сумма</span>
                  <NumericDraftInput
                    placeholder="—"
                    value={person.paymentAmount}
                    onCommit={(paymentAmount) => void onSetPayment(person.userId, { paymentAmount })}
                  />
                </label>
              </div>
            </section>
          )}

          {isStudent && (
            <section className="profile-section">
              <h3>
                <Icon name="clipboard" size={14} />
                Домашняя работа
                {tasks && tasks.length > 0 && (
                  <em className="field-hint">
                    {' '}
                    осталось {pending} из {tasks.length}
                  </em>
                )}
              </h3>
              {tasks === null ? (
                <p className="org-members-hint">Загрузка…</p>
              ) : tasksFailed ? (
                <p className="form-error" role="alert">
                  Не удалось загрузить задания
                </p>
              ) : tasks.length === 0 ? (
                <p className="org-members-hint">Заданий пока нет.</p>
              ) : (
                <ul className="profile-tasks">
                  {tasks.slice(0, 8).map((task) => (
                    <li key={task.id}>
                      <button type="button" onClick={() => onOpenTask(task.id)}>
                        {task.subject && <span className="subject-badge">{task.subject}</span>}
                        <span className="profile-task-title">{task.title}</span>
                        <em>
                          {task.dueAt ? formatDateShort(task.dueAt) : 'без срока'}
                          {task.done ? ' · сдано' : ''}
                        </em>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        <footer className="profile-foot">
          {isStudent && (
            <button type="button" className="primary-btn" onClick={() => onGiveTask(person.userId)}>
              <Icon name="plus" size={15} />
              Дать задание
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="ghost-btn" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
}
